import { Anchor, Avatar, Group, Loader, Stack, Table, Text, Title } from '@mantine/core';
import { Link, useParams } from 'react-router';

import { RolesEditor } from '../../features/roles';
import { useAccount } from '../../features/users';
import { ApiError } from '../../shared/api/client';
import { PROVIDER_LABEL, formatDateTime } from '../../shared/format';
import { EmptyState, ErrorState } from '../../shared/ui';

/**
 * Карточка аккаунта (CAP-3) с действиями над ролями (CAP-4). Всё, что здесь показано,
 * пришло из `GET /admin/users/{userId}`; после выдачи или отзыва роли карточка перечитывается
 * — результат показывает сервер, а не оптимистичная правка.
 */
export function UserCardPage() {
  const { userId = '' } = useParams();
  const account = useAccount(userId);

  return (
    <Stack gap="md">
      <Anchor component={Link} to="/users" size="sm">
        ← Пользователи
      </Anchor>
      {account.isPending ? <Loader /> : null}
      {account.isError ? (
        account.error instanceof ApiError && account.error.status === 404 ? (
          <EmptyState>Аккаунта с таким идентификатором нет.</EmptyState>
        ) : (
          <ErrorState error={account.error} />
        )
      ) : null}
      {account.data ? (
        <>
          <Group>
            <Avatar src={account.data.avatarUrl} radius="xl" size="lg" />
            <Title order={2}>{account.data.displayName ?? 'Без имени'}</Title>
          </Group>
          <Table w={560}>
            <Table.Tbody>
              <Table.Tr>
                <Table.Th w={180}>Идентификатор</Table.Th>
                <Table.Td>
                  <code>{account.data.id}</code>
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>Статус</Table.Th>
                <Table.Td>{account.data.status}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>Вход</Table.Th>
                <Table.Td>
                  {account.data.providers.length === 0
                    ? '—'
                    : account.data.providers
                        .map((provider) => PROVIDER_LABEL[provider] ?? provider)
                        .join(', ')}
                </Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>Создан</Table.Th>
                <Table.Td>{formatDateTime(account.data.createdAt)}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>Обновлён</Table.Th>
                <Table.Td>{formatDateTime(account.data.updatedAt)}</Table.Td>
              </Table.Tr>
              <Table.Tr>
                <Table.Th>Роли</Table.Th>
                <Table.Td>
                  <RolesEditor userId={account.data.id} held={account.data.roles} />
                </Table.Td>
              </Table.Tr>
            </Table.Tbody>
          </Table>
          <Text size="xs" c="dimmed">
            Каждая выдача и отзыв роли записываются в журнал аудита.
          </Text>
        </>
      ) : null}
    </Stack>
  );
}
